import { useMemo } from 'react';
import { Center, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import useFetchUserVouchers from 'app/hooks/useFetchUserVouchers/useFetchUserVouchers';
import getVoucherColumns from 'app/utils/table/vouchers';

import { VoucherTableProps, Voucher } from './VoucherTable.types';

function VoucherTable({ individual }: VoucherTableProps) {
  const {
    data: voucherResponse,
    isLoading: isLoadingVouchers,
    error: vouchersError,
  } = useFetchUserVouchers(individual?.id);

  const vouchers: Voucher[] = useMemo(
    () =>
      voucherResponse?.list.map((v) => ({
        ...v,
        individualId: v.individualId || individual?.id || '',
      })) || [],
    [voucherResponse, individual]
  );

  const columns = getVoucherColumns();
  const table = useReactTable<Voucher>({
    data: vouchers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {vouchersError && (
        <Text color="red.500">Error loading vouchers: {vouchersError.message}</Text>
      )}
      {isLoadingVouchers || !individual ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <Table variant="simple">
          <Thead>
            {table.getHeaderGroups().map((hg) => (
              <Tr key={hg.id}>
                {hg.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    onClick={
                      cell.column.columnDef.meta?.getCellContext?.(cell.getContext())?.onClick
                    }
                    style={cell.column.columnDef.meta?.getCellContext?.(cell.getContext())?.style}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </>
  );
}

export default VoucherTable;
